import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'
import type { Week } from './auto-place'

/**
 * The coach's manual fold choices for elapsed weeks (LG-544 §2).
 *
 * `Week.folded` stays presentation-only (see its doc in `auto-place.ts`), so a
 * coach folding a week by hand is a viewing preference, not plan state: no
 * column, no procedure, just localStorage. Storage is already per browser
 * profile — i.e. per coach — so the key only has to add the engagement, which
 * keeps one client's folds off another client's board.
 *
 * A map rather than a set: an entry means "the coach said so", and it has to
 * be able to say *un*folded too, because the empty-week rule folds some weeks
 * by default and a coach who opens one back up must keep it open.
 */
export type WeekFoldOverrides = ReadonlyMap<string, boolean>

const WEEK_FOLD_STORAGE_PREFIX = 'bctci:curriculum:folded-weeks'

const weekFoldStorageKey = (engagementId: string): string =>
  `${WEEK_FOLD_STORAGE_PREFIX}:${engagementId}`

export const readStoredWeekFolds = (engagementId: string): WeekFoldOverrides =>
  readLocalPreference<WeekFoldOverrides>(
    weekFoldStorageKey(engagementId),
    (raw) => {
      const parsed: unknown = JSON.parse(raw)
      if (parsed === null || typeof parsed !== 'object') return null
      // Stored as the map is held — one boolean per week id, so "folded" and
      // "unfolded" cannot both be claimed for the same week and no reader has
      // to invent a tie-break. Entries that are not booleans are dropped
      // rather than taking the whole preference down with them.
      return new Map(
        Object.entries(parsed as Record<string, unknown>).flatMap(
          ([weekId, folded]): [string, boolean][] =>
            typeof folded === 'boolean' ? [[weekId, folded]] : [],
        ),
      )
    },
    new Map(),
  )

export const writeStoredWeekFolds = (
  engagementId: string,
  overrides: WeekFoldOverrides,
): void =>
  writeLocalPreference(
    weekFoldStorageKey(engagementId),
    JSON.stringify(Object.fromEntries(overrides)),
  )

/**
 * Whether a week renders folded: the coach's explicit choice if they made
 * one, otherwise whatever the board decided on its own (the persisted board
 * folds empty elapsed weeks; see `scheduleState`).
 *
 * Overrides count for elapsed weeks only. The disclosure that hides folded
 * weeks is labelled "earlier week(s)" and is the only route back to one, so a
 * stored id that no longer names an elapsed week is ignored rather than
 * hiding a week the coach is still planning.
 */
export const foldedWeek = (
  week: Pick<Week, 'id' | 'elapsed' | 'folded'>,
  overrides: WeekFoldOverrides,
): boolean => (week.elapsed ? overrides.get(week.id) : undefined) ?? week.folded

/**
 * The overrides after the coach toggles this week's fold — unchanged if the
 * week may not be folded at all. Takes previous overrides rather than
 * returning a single entry, so a caller inside a state updater can hand it
 * committed state and two toggles in one commit net to nothing.
 */
export const withWeekFoldToggled = (
  overrides: WeekFoldOverrides,
  week: Pick<Week, 'id' | 'elapsed' | 'folded'>,
): WeekFoldOverrides =>
  week.elapsed
    ? new Map(overrides).set(week.id, !foldedWeek(week, overrides))
    : overrides
